import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Calendar, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function AttendancePage() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSession, setSelectedSession] = useState('');
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const [classesRes, sessionsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/academic-sessions')
      ]);
      setClasses(classesRes.data);
      setSessions(sessionsRes.data);
      if (sessionsRes.data.length > 0 && !selectedSession) {
        setSelectedSession(sessionsRes.data[sessionsRes.data.length - 1].sessionYear);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await api.get(`/sections?classId=${classId}`);
      setSections(response.data);
    } catch (error) {
      toast.error('Failed to fetch sections');
    }
  };

  const fetchStudents = async () => {
    if (!selectedClass || !selectedSection) return;
    
    try {
      const [studentsRes, attendanceRes] = await Promise.all([
        api.get(`/students?classId=${selectedClass}&sectionId=${selectedSection}`),
        api.get(`/attendance?classId=${selectedClass}&date=${selectedDate}`)
      ]);
      
      setStudents(studentsRes.data);
      
      const attMap = {};
      attendanceRes.data.forEach(att => {
        attMap[att.studentId] = att.status;
      });
      setAttendance(attMap);
    } catch (error) {
      toast.error('Failed to fetch students');
    }
  };

  useEffect(() => {
    if (selectedClass && selectedSection) {
      fetchStudents();
    }
  }, [selectedClass, selectedSection, selectedDate]);

  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    setSelectedSection('');
    setStudents([]);
    setAttendance({});
    if (classId) {
      fetchSections(classId);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleSubmit = async () => {
    if (students.length === 0) {
      toast.error('No students to mark attendance for');
      return;
    }

    const attendanceData = students.map(student => ({
      studentId: student.id,
      status: attendance[student.id] || 'absent'
    }));

    try {
      await api.post('/attendance/bulk', {
        classId: selectedClass,
        sectionId: selectedSection,
        date: selectedDate,
        attendance: attendanceData
      });
      toast.success('Attendance marked successfully');
    } catch (error) {
      toast.error('Failed to mark attendance');
    }
  };

  const getStats = () => {
    const present = Object.values(attendance).filter(s => s === 'present').length;
    const absent = Object.values(attendance).filter(s => s === 'absent').length;
    const late = Object.values(attendance).filter(s => s === 'late').length;
    return { present, absent, late };
  };

  const stats = getStats();

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-outfit font-semibold text-2xl text-slate-800">Attendance Management</h2>
        <p className="text-slate-500">Mark daily attendance for students</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Session</label>
              <select
                value={selectedSession}
                onChange={(e) => {
                  setSelectedSession(e.target.value);
                  setSelectedClass('');
                  setSelectedSection('');
                  setStudents([]);
                }}
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              >
                <option value="">Select Session</option>
                {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
              <select
                value={selectedClass}
                onChange={(e) => handleClassChange(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
                disabled={!selectedSession}
              >
                <option value="">Select Class</option>
                {classes.filter(c => c.sessionYear === selectedSession).map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
              </select>
            </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Section</label>
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              disabled={!selectedClass}
            >
              <option value="">Select Section</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.sectionName}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSubmit}
              disabled={students.length === 0}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-full px-6 py-2.5 font-medium shadow-sm active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Attendance
            </button>
          </div>
        </div>
      </div>

      {students.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <p className="text-sm text-slate-500">Total Students</p>
            <p className="text-2xl font-outfit font-bold text-slate-800">{students.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-4">
            <p className="text-sm text-green-600">Present</p>
            <p className="text-2xl font-outfit font-bold text-green-600">{stats.present}</p>
          </div>
          <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-4">
            <p className="text-sm text-red-600">Absent</p>
            <p className="text-2xl font-outfit font-bold text-red-600">{stats.absent}</p>
          </div>
          <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-4">
            <p className="text-sm text-orange-600">Late</p>
            <p className="text-2xl font-outfit font-bold text-orange-600">{stats.late}</p>
          </div>
        </div>
      )}

      {students.length > 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-4">Mark Attendance</h3>
          <div className="space-y-2">
            {students.map(student => (
              <div key={student.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div>
                  <p className="font-medium text-slate-800">{student.name}</p>
                  <p className="text-sm text-slate-500">Roll No: {student.rollNumber}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAttendanceChange(student.id, 'present')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      attendance[student.id] === 'present'
                        ? 'bg-green-600 text-white'
                        : 'bg-white border border-green-200 text-green-600 hover:bg-green-50'
                    }`}
                  >
                    Present
                  </button>
                  <button
                    onClick={() => handleAttendanceChange(student.id, 'absent')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      attendance[student.id] === 'absent'
                        ? 'bg-red-600 text-white'
                        : 'bg-white border border-red-200 text-red-600 hover:bg-red-50'
                    }`}
                  >
                    Absent
                  </button>
                  <button
                    onClick={() => handleAttendanceChange(student.id, 'late')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      attendance[student.id] === 'late'
                        ? 'bg-orange-600 text-white'
                        : 'bg-white border border-orange-200 text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    Late
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <Calendar className="w-16 h-16 text-indigo-200 mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-2">Select Class and Section</h3>
          <p className="text-slate-500">Choose a class, section, and date to mark attendance</p>
        </div>
      )}
    </div>
  );
}
